// src/oura/components/aeo/AssessmentResults.tsx
//
// AC: "Results view renders per-provider mention_rate/citation_rate/
// responses_sampled, share_of_voice, audit (respecting audit.mode),
// maturity_level/maturity_label, top_competitors, actual_cost_usd, and
// report_url." (#3065)
// AC: "partial → results + degraded-mode note; failed → error_code/
// error_detail + retry; 0% ChatGPT framed as opportunity."

import {
    AlertTriangleIcon,
    BotIcon,
    DollarSignIcon,
    ExternalLinkIcon,
    FileTextIcon,
    GaugeIcon,
    RefreshCwIcon,
    SparklesIcon,
    TrendingUpIcon,
    TargetIcon,
    UsersIcon,
    XCircleIcon,
} from "lucide-react";
import { Card, CardBody, CardHeader } from "../../primitives/Card";
import { Button } from "../../primitives/Button";
import { Chip } from "../../primitives/Chip";
import { StatTile } from "../../primitives/StatTile";
import { cn } from "../../lib/cn";
import type { AeoAssessment, AeoProviderVisibility } from "@service/aeo";

const pct = (v: number) => `${Math.round(v * 100)}%`;

function ProviderCard({
    label,
    icon: Icon,
    stats,
    opportunityNote,
}: {
    label: string;
    icon: typeof BotIcon;
    stats: AeoProviderVisibility;
    /** Shown when a 0% mention rate should read as opportunity, not failure. */
    opportunityNote?: string;
}) {
    const zeroMention = stats.mention_rate === 0;
    return (
        <div className="rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-semibold text-gray-900">{label}</span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div>
                    <div
                        className={cn(
                            "text-xl font-semibold tabular-nums",
                            zeroMention ? "text-amber-600" : "text-gray-900",
                        )}
                    >
                        {pct(stats.mention_rate)}
                    </div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500">Mention rate</div>
                </div>
                <div>
                    <div className="text-xl font-semibold tabular-nums text-gray-900">
                        {pct(stats.citation_rate)}
                    </div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500">Citation rate</div>
                </div>
                <div>
                    <div className="text-xl font-semibold tabular-nums text-gray-900">{stats.responses_sampled}</div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500">Sampled</div>
                </div>
            </div>
            {zeroMention && opportunityNote && (
                <div className="mt-3 flex items-start gap-1.5 rounded-md bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
                    <SparklesIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                    <span>{opportunityNote}</span>
                </div>
            )}
        </div>
    );
}

export function AssessmentResults({
    assessment,
    onRetry,
}: {
    assessment: AeoAssessment;
    /** Failed state only — re-runs the same input via the create form. */
    onRetry?: () => void;
}) {
    if (assessment.status === "failed") {
        return (
            <Card>
                <CardHeader title="Assessment failed" icon={XCircleIcon} />
                <CardBody className="pt-2">
                    <div className="rounded-lg border border-rose-200 bg-rose-50 p-4">
                        <div className="flex items-center gap-2">
                            <Chip className="border-rose-200 bg-white text-rose-700">
                                {assessment.error_code ?? "UNKNOWN_ERROR"}
                            </Chip>
                        </div>
                        <p className="mt-2 text-sm text-rose-700">
                            {assessment.error_detail ?? "The assessment could not complete. Please retry."}
                        </p>
                    </div>
                    {onRetry && (
                        <div className="mt-4 flex justify-end">
                            <Button variant="outline" writes onClick={onRetry}>
                                <RefreshCwIcon className="h-4 w-4" />
                                Retry
                            </Button>
                        </div>
                    )}
                </CardBody>
            </Card>
        );
    }

    const { results } = assessment;
    if (!results) return null; // status is complete/partial but results missing — nothing sane to render

    const isPartial = assessment.status === "partial";

    return (
        <div className="space-y-4">
            {isPartial && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div>
                        <p className="font-medium">
                            {assessment.error_code === "ENGINE_STUBBED"
                                ? "This is a preview run — the AI-visibility engine isn't provisioned yet."
                                : "This run finished in degraded mode."}
                        </p>
                        <p className="mt-0.5 text-amber-700">
                            {assessment.error_code === "ENGINE_STUBBED"
                                ? "The audit numbers below are real; visibility numbers are placeholders until the engine goes live — no FE change needed when it does."
                                : `Results are usable but incomplete (audit mode: ${results.audit.mode}).`}
                        </p>
                    </div>
                </div>
            )}

            <Card>
                <CardHeader
                    title="AI visibility"
                    description="How often each engine names this business when customers ask."
                    icon={TargetIcon}
                />
                <CardBody className="pt-2">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <ProviderCard
                            label="ChatGPT (OpenAI)"
                            icon={BotIcon}
                            stats={results.visibility.openai}
                            opportunityNote="A 0% ChatGPT baseline is normal for SMBs — this is the growth opportunity, not a failed run."
                        />
                        <ProviderCard label="Gemini" icon={SparklesIcon} stats={results.visibility.gemini} />
                    </div>
                    <div className="mt-3">
                        <StatTile
                            label="Share of voice"
                            value={pct(results.visibility.share_of_voice)}
                            icon={TrendingUpIcon}
                            tooltip="This business's mentions as a share of all mentions across the tracked competitor set."
                        />
                    </div>
                </CardBody>
            </Card>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <StatTile
                    label="Site audit score"
                    value={results.audit.overall_score.toFixed(1)}
                    icon={GaugeIcon}
                    badge={results.audit.mode === "visibility_only" ? "visibility only" : `${results.audit.pages_scraped} pages`}
                    tooltip={
                        results.audit.mode === "visibility_only"
                            ? "Audit ran in visibility-only mode — the full site crawl did not complete."
                            : `Full site audit across ${results.audit.pages_scraped} pages.`
                    }
                />
                <StatTile
                    label="Maturity level"
                    value={`Level ${results.maturity_level}`}
                    badge={results.maturity_label}
                    icon={SparklesIcon}
                    tooltip="Overall AI-visibility maturity, from the combined audit + engine results."
                />
                <StatTile
                    label="Assessment cost"
                    value={`$${results.actual_cost_usd.toFixed(2)}`}
                    icon={DollarSignIcon}
                    tooltip="Actual cost incurred running this assessment."
                />
            </div>

            <Card>
                <CardHeader title="Top competitors" icon={UsersIcon} />
                <CardBody className="pt-2">
                    {results.top_competitors.length === 0 ? (
                        <p className="text-sm text-gray-500">No competitor mentions surfaced in this run.</p>
                    ) : (
                        <div className="divide-y divide-gray-200">
                            {results.top_competitors.map((c) => (
                                <div key={c.name} className="flex items-center justify-between py-2 text-sm">
                                    <span className="text-gray-900">{c.name}</span>
                                    <span className="tabular-nums text-gray-500">
                                        appeared in {c.queries_appeared} queries
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </CardBody>
            </Card>

            <Card>
                <CardHeader title="Full report" icon={FileTextIcon} />
                <CardBody className="pt-2">
                    {assessment.report_url ? (
                        <a
                            href={assessment.report_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline"
                        >
                            View PDF report
                            <ExternalLinkIcon className="h-3.5 w-3.5" />
                        </a>
                    ) : (
                        <p className="text-sm text-gray-500">
                            No report available yet for this run{isPartial ? " (degraded mode)" : ""}.
                        </p>
                    )}
                </CardBody>
            </Card>
        </div>
    );
}
